import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

const LOGO_URL = "/images/Logo.png";

const navLinks = [
  { label: "Accueil", href: "/" },
  {
    label: "Le Miscanthus",
    href: "#",
    children: [
      { label: "La plante", href: "/le-miscanthus" },
      { label: "La paille de Miscanthus", href: "/la-paille" },
      { label: "Les rhizomes & la plantation", href: "/les-rhizomes" },
    ],
  },
  { label: "Nos Produits", href: "/produits" },
  { label: "L'Exploitation", href: "/exploitation" },
  { label: "Contact", href: "/contact" },
];

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileDropdown, setMobileDropdown] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setDropdownOpen(false);
  }, [location]);

  const isActive = (href) => location.pathname === href;

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-beige/95 backdrop-blur-sm shadow-sm" : "bg-beige"
      }`}
      style={{ borderBottom: "1px solid #d8d1c4" }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-28 md:h-28">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group" data-testid="navbar-logo">
            <img src={LOGO_URL} alt="Torcé Biomasse Energie" className="h-24 w-auto" />
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) =>
              link.children ? (
                <div key={link.label} className="relative" onMouseEnter={() => setDropdownOpen(true)} onMouseLeave={() => setDropdownOpen(false)}>
                  <button
                    className="flex items-center gap-1 px-3 py-2 text-sm font-body rounded-md transition-colors"
                    style={{ color: "#3a2b24", fontWeight: 500 }}
                    data-testid="navbar-miscanthus-dropdown"
                  >
                    {link.label}
                    <ChevronDown size={14} className={`transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
                  </button>
                  {dropdownOpen && (
                    <div
                      className="absolute top-full left-0 w-56 rounded-lg shadow-lg py-1 z-50"
                      style={{ background: "#f5f2ec", border: "1px solid #d8d1c4" }}
                    >
                      {link.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          className="block px-4 py-2.5 text-sm transition-colors hover:bg-beige-muted"
                          style={{ color: "#3a2b24" }}
                          data-testid={`navbar-link-${child.href.replace("/", "")}`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  className="px-3 py-2 text-sm rounded-md transition-colors"
                  style={{
                    color: isActive(link.href) ? "#2d5016" : "#3a2b24",
                    fontWeight: isActive(link.href) ? 600 : 500,
                    borderBottom: isActive(link.href) ? "2px solid #2d5016" : "2px solid transparent",
                  }}
                  data-testid={`navbar-link-${link.href.replace("/", "") || "home"}`}
                >
                  {link.label}
                </Link>
              )
            )}
            <Link to="/contact" className="btn-primary ml-3 px-4 py-2 text-sm rounded-md" data-testid="navbar-cta">
              Demander un devis
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            className="lg:hidden p-2 rounded-md"
            style={{ color: "#3a2b24" }}
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="navbar-hamburger"
            aria-label="Menu"
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden border-t" style={{ background: "#f5f2ec", borderColor: "#d8d1c4" }} data-testid="navbar-mobile-menu">
          <div className="px-4 py-3 space-y-1">
            {navLinks.map((link) =>
              link.children ? (
                <div key={link.label}>
                  <button
                    className="flex items-center justify-between w-full px-3 py-2.5 text-sm rounded-md"
                    style={{ color: "#3a2b24", fontWeight: 500 }}
                    onClick={() => setMobileDropdown(!mobileDropdown)}
                  >
                    {link.label}
                    <ChevronDown size={14} className={`transition-transform ${mobileDropdown ? "rotate-180" : ""}`} />
                  </button>
                  {mobileDropdown && (
                    <div className="pl-4 space-y-1">
                      {link.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          className="block px-3 py-2 text-sm rounded-md"
                          style={{ color: "#3a2b24" }}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  className="block px-3 py-2.5 text-sm rounded-md"
                  style={{ color: isActive(link.href) ? "#2d5016" : "#3a2b24", fontWeight: 500 }}
                >
                  {link.label}
                </Link>
              )
            )}
            <Link
              to="/contact"
              className="btn-primary block text-center mt-2 py-2.5 rounded-md text-sm"
              data-testid="navbar-cta-mobile"
            >
              Demander un devis
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
